<template>
  <div class="tc-verify-day-strip" data-testid="verify-day-strip" role="list" aria-label="Per-day total hours">
    <div
      v-for="day in days"
      :key="day.dayIndex"
      class="tc-verify-day-cell"
      :class="{
        'tc-verify-day-cell--weekend': day.isWeekend,
        'tc-verify-day-cell--filled': day.hours > 0,
      }"
      :data-testid="`verify-day-cell-${day.dayIndex}`"
      role="listitem"
      :aria-label="`${day.label}: ${day.hours.toFixed(2)} hours`"
    >
      <span class="tc-verify-day-cell-num">{{ day.dayOfMonth }}</span>
      <span class="tc-verify-day-cell-hours">{{ day.hours > 0 ? day.hours.toFixed(2) : '–' }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { formatMonthDay, periodDayCount } from '../utils/period';

interface DraftLike {
  dayIndex?: number;
  hours?: number;
}

const props = defineProps<{
  /** ISO YYYY-MM-DD of the period start (day index 0). */
  periodStart: string;
  /** ISO YYYY-MM-DD of the period end. With the start, sets how many cells the strip has. */
  periodEnd: string;
  /** Drafts to total per day. dayIndex is 0-based on the period (matches Pinia store). */
  drafts: DraftLike[];
}>();

const dayCount = computed(() => periodDayCount(props.periodStart, props.periodEnd));

interface DayCell {
  dayIndex: number;
  dayOfMonth: number;
  isWeekend: boolean;
  hours: number;
  label: string;
}


const days = computed((): DayCell[] => {
  const totals = new Array<number>(dayCount.value).fill(0);
  for (const d of props.drafts) {
    if (d.dayIndex == null || d.hours == null) continue;
    if (d.dayIndex < 0 || d.dayIndex >= dayCount.value) continue;
    totals[d.dayIndex] += d.hours;
  }

  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(props.periodStart);
  if (!m) {
    return totals.map((hours, i) => ({
      dayIndex: i,
      dayOfMonth: i + 1,
      isWeekend: false,
      hours,
      label: `Day ${i + 1}`,
    }));
  }

  const base = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return totals.map((hours, i) => {
    const date = new Date(base);
    date.setUTCDate(date.getUTCDate() + i);
    const dow = date.getUTCDay();
    return {
      dayIndex: i,
      dayOfMonth: date.getUTCDate(),
      isWeekend: dow === 0 || dow === 6,
      hours,
      label: formatMonthDay(date.toISOString().slice(0, 10)),
    };
  });
});
</script>
